import { ApiHistoryRow, ApiReport, HistoryRow, MonthlyState, NotCountedItem, ReportData } from "../types";

const NOT_COUNTED_LABELS: Record<NotCountedItem["key"], string> = {
  unpaid_expenses: "unpaid expense",
  unverified_income: "unverified income entry",
  unapproved_deposits: "unapproved deposit",
};

const normalizeMonthly = (api: ApiReport): MonthlyState | null => {
  const { reconciliation, mortgage, partner_share: share, month } = api;
  if (!reconciliation || !mortgage || !share || !month || !api.carry_forward_source) return null;

  return {
    carryForwardSource: api.carry_forward_source,
    reconciliation,
    mortgage: {
      categoryId: mortgage.category_id,
      categoryName: mortgage.category_name,
      categoryAuto: mortgage.category_auto,
      categoryTotal: mortgage.category_total,
      overrideAmount: mortgage.override_amount,
      effectiveAmount: mortgage.effective_amount,
      categoryOptions: mortgage.category_options,
    },
    partnerShare: {
      applicable: share.applicable,
      ready: share.ready,
      message: share.message,
      missing: share.missing ?? [],
      cashHolderId: share.cash_holder_id,
      cashHolderName: share.cash_holder_name,
      cashOnHand: share.cash_on_hand,
      cashOnHandSource: share.cash_on_hand_source,
      mortgageAmount: share.mortgage_amount,
      partners: share.partners.map((p) => ({
        id: p.id,
        name: p.name,
        ownershipPct: p.ownership_pct,
        depositBalance: p.deposit_balance,
        cashOnHand: p.cash_on_hand,
        adjustedBalance: p.adjusted_balance,
        contribution: p.contribution,
        finalBalance: p.final_balance,
      })),
    },
    month: {
      id: month.id,
      status: month.status,
      openingBalance: month.opening_balance,
      closingBalance: month.closing_balance,
      closedAt: month.closed_at,
      closedBy: month.closed_by,
      reopenedAt: month.reopened_at,
      reopenedBy: month.reopened_by,
      stale: month.stale,
    },
    laterClosedMonths: api.later_closed_months ?? [],
    closeBlockers: api.close_blockers ?? [],
    permissions: {
      canEdit: !!api.permissions?.can_edit,
      canClose: !!api.permissions?.can_close,
      canReopen: !!api.permissions?.can_reopen,
    },
  };
};

/**
 * The one place the API response is turned into the shape the screen, the
 * PDF and the print layout render. Only renames and reshapes — every amount
 * comes straight from the backend.
 */
export const normalizeReport = (api: ApiReport): ReportData => ({
  project: api.project,
  period: {
    mode: api.period.month ? "month" : "range",
    year: api.period.year ?? null,
    month: api.period.month ?? null,
    startDate: api.period.start_date,
    endDate: api.period.end_date,
    label: api.period.label,
  },
  carryForward: api.summary.carry_forward,
  operatingIncomeTotal: api.summary.income_total,
  depositTotal: api.summary.deposit_total,
  incomeTotal: api.summary.inflow_total,
  expenseTotal: api.summary.expense_total,
  netForPeriod: api.summary.net_for_period,
  balanceOnHand: api.summary.balance_on_hand,
  incomeBreakdown: api.income_breakdown,
  depositBreakdown: api.deposit_breakdown,
  expenseBreakdown: api.expense_breakdown,
  ledger: api.ledger.map((row) => ({
    id: row.id,
    sourceId: row.source_id,
    date: row.date,
    kind: row.kind,
    label: row.label,
    personName: row.person_name,
    description: row.description,
    attachments: row.attachments,
    expenseAmount: row.expense_amount,
    incomeAmount: row.income_amount ?? row.deposit_amount,
    runningBalance: row.running_balance,
  })),
  notCounted: (Object.keys(NOT_COUNTED_LABELS) as NotCountedItem["key"][])
    .map((key) => ({ key, label: NOT_COUNTED_LABELS[key], ...api.not_counted[key] }))
    .filter((item) => item.count > 0),
  monthly: normalizeMonthly(api),
});

export const normalizeHistory = (rows: ApiHistoryRow[]): HistoryRow[] =>
  rows.map((r) => ({
    year: r.period.year,
    month: r.period.month,
    label: r.period.label,
    carryForward: r.carry_forward,
    incomeTotal: r.inflow_total,
    expenseTotal: r.expense_total,
    closingBalance: r.closing_balance,
    actualBalance: r.actual_balance,
    difference: r.difference,
    reconciliationStatus: r.reconciliation_status,
    monthStatus: r.month_status,
  }));
